/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Hook for Dynamic Workspace Template Resolution (Task 3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { useState, useEffect, useMemo } from 'react';
import { getApiBaseUrl } from '../lib/api-client';
import { getWorkspaceTemplate, isValidWorkspaceTemplate } from '../templates/registry';
import type { DashboardLayoutProps } from '../layouts/DashboardLayout';

export interface ActiveWorkspaceTemplateResult {
  templateId: string;
  LayoutComponent: React.ComponentType<DashboardLayoutProps>;
  isLoading: boolean;
  source: 'query' | 'session' | 'api' | 'default';
}

const SESSION_STORAGE_KEY = 'sophia_workspace_preview_template';

/**
 * Resolves the currently active template for User Workspace.
 *
 * Priority order:
 * 1. URL Query Parameter (?template=<slug>) - Immediate Live Preview
 * 2. Session Storage cache (persists preview while navigating within session)
 * 3. Cloudflare Edge Public API (GET /api/v1/public/templates -> userWorkspace.activeTemplate)
 * 4. Fallback default ('default')
 */
export function useActiveWorkspaceTemplate(): ActiveWorkspaceTemplateResult {
  // 1. Initial synchronous check from URL or SessionStorage
  const initialResolution = useMemo<{ templateId: string; source: 'query' | 'session' | 'default' }>(() => {
    if (typeof window === 'undefined') {
      return { templateId: 'default', source: 'default' };
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const queryTemplate = urlParams.get('template');

      if (queryTemplate && isValidWorkspaceTemplate(queryTemplate)) {
        sessionStorage.setItem(SESSION_STORAGE_KEY, queryTemplate.trim().toLowerCase());
        return { templateId: queryTemplate.trim().toLowerCase(), source: 'query' };
      }

      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored && isValidWorkspaceTemplate(stored)) {
        return { templateId: stored.trim().toLowerCase(), source: 'session' };
      }
    } catch {
      // Ignore storage access errors in restricted iframe/browser environments
    }

    return { templateId: 'default', source: 'default' };
  }, []);

  const [templateId, setTemplateId] = useState<string>(initialResolution.templateId);
  const [source, setSource] = useState<'query' | 'session' | 'api' | 'default'>(initialResolution.source);
  const [isLoading, setIsLoading] = useState<boolean>(initialResolution.source === 'default');

  // 2. Asynchronously fetch Edge Active Template if no query/session override is active
  useEffect(() => {
    let mounted = true;

    // If already resolved via query parameter or session storage, don't overwrite with API
    if (initialResolution.source === 'query' || initialResolution.source === 'session') {
      setIsLoading(false);
      return;
    }

    const fetchActiveTemplate = async () => {
      try {
        const apiOrigin = getApiBaseUrl();
        const res = await fetch(`${apiOrigin}/api/v1/public/templates`, {
          headers: {
            Accept: 'application/json',
          },
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const json = (await res.json()) as any;
        const serverActiveId =
          (typeof json?.data?.userWorkspace === 'string' ? json.data.userWorkspace : json?.data?.userWorkspace?.activeTemplate) ||
          json?.data?.activeTemplates?.userWorkspace ||
          json?.data?.user_workspace ||
          json?.data?.userWorkspace?.activeTemplateId;

        if (mounted && serverActiveId && typeof serverActiveId === 'string') {
          const normalized = serverActiveId.trim().toLowerCase();
          setTemplateId(normalized);
          setSource('api');
        }
      } catch (err) {
        // Fallback remains 'default'
        console.warn('[useActiveWorkspaceTemplate] Public templates fetch error, using default:', err);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    void fetchActiveTemplate();

    return () => {
      mounted = false;
    };
  }, [initialResolution.source]);

  // Resolve the actual component
  const LayoutComponent = useMemo(() => {
    return getWorkspaceTemplate(templateId);
  }, [templateId]);

  return {
    templateId,
    LayoutComponent,
    isLoading,
    source,
  };
}
